import React, { useEffect, useState } from 'react';
import { getApiBaseUrl, readJsonResponse } from '../apiBase';
import {
    AS_REPAIR_BUILTIN_KEYS,
    buildDefaultAsRepairFormConfig,
    normalizeAsRepairFormConfig,
} from '../utils/asRepairFormConfig';
import {
    DEFAULT_FREE_REPAIR_QTY_FORMULA,
    DEFAULT_INCOMING_QTY_FORMULA,
    suggestAsRepairAutoFormula,
} from '../utils/asRepairCalculations';
import {
    buildDefaultProductRegistrationConfig,
    normalizeProductRegistrationConfig,
    PRODUCT_TYPES,
} from '../utils/productRegistrationConfig';
import './ProductRegistrationSettingsPage.css';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const TYPE_LABELS = {
    adjuster: '조절기',
    controller: '제어기',
    asRepair: 'A/S 리스트',
    asRepairPersonal: 'A/S 개인 관리',
};

const AS_REPAIR_INPUT_TYPE_OPTIONS = [
    { value: 'text', label: '직접 입력' },
    { value: 'textarea', label: '장문 입력' },
    { value: 'date', label: '날짜 입력' },
    { value: 'status', label: '상태 선택' },
    { value: 'quantity', label: '수량 입력' },
    { value: 'dropdown', label: '드롭다운' },
    { value: 'auto', label: '자동 입력' },
];

const ProductRegistrationSettingsPage = ({
    currentUserName,
    initialConfig,
    initialAsRepairConfig,
    initialAsRepairPersonalConfig,
    onConfigSaved,
    showAppToast,
}) => {
    const [activeType, setActiveType] = useState('adjuster');
    const [draft, setDraft] = useState(() => normalizeProductRegistrationConfig(initialConfig));
    const [asRepairDraft, setAsRepairDraft] = useState(() => normalizeAsRepairFormConfig(initialAsRepairConfig));
    const [asRepairPersonalDraft, setAsRepairPersonalDraft] = useState(() => (
        normalizeAsRepairFormConfig(initialAsRepairPersonalConfig)
    ));
    const [busy, setBusy] = useState(false);
    const [loadError, setLoadError] = useState('');

    const isAsRepairTab = activeType === 'asRepair';
    const isAsRepairPersonalTab = activeType === 'asRepairPersonal';
    const isAnyAsRepairTab = isAsRepairTab || isAsRepairPersonalTab;

    useEffect(() => {
        setDraft(normalizeProductRegistrationConfig(initialConfig));
        setAsRepairDraft(normalizeAsRepairFormConfig(initialAsRepairConfig));
        setAsRepairPersonalDraft(normalizeAsRepairFormConfig(initialAsRepairPersonalConfig));
    }, [initialConfig, initialAsRepairConfig, initialAsRepairPersonalConfig]);

    const reload = async () => {
        setLoadError('');
        const res = await fetch(`${getApiBaseUrl()}/product-registration-config`, { cache: 'no-store' });
        const data = await readJsonResponse(res);
        const productConfig = normalizeProductRegistrationConfig(data);
        const asRepairConfig = normalizeAsRepairFormConfig(data?.asRepair);
        const asRepairPersonalConfig = normalizeAsRepairFormConfig(data?.asRepairPersonal);
        setDraft(productConfig);
        setAsRepairDraft(asRepairConfig);
        setAsRepairPersonalDraft(asRepairPersonalConfig);
        onConfigSaved(productConfig, asRepairConfig, asRepairPersonalConfig);
    };

    const productFields = draft[activeType]?.fields || [];
    const asRepairFields = (isAsRepairPersonalTab ? asRepairPersonalDraft : asRepairDraft).fields || [];

    const setActiveAsRepairDraft = (updater) => {
        if (isAsRepairPersonalTab) {
            setAsRepairPersonalDraft(updater);
            return;
        }
        setAsRepairDraft(updater);
    };

    const updateProductField = (index, patch) => {
        setDraft((prev) => {
            const next = { ...prev, [activeType]: { fields: [...(prev[activeType]?.fields || [])] } };
            next[activeType].fields[index] = { ...next[activeType].fields[index], ...patch };
            return next;
        });
    };

    const updateAsRepairField = (index, patch) => {
        setActiveAsRepairDraft((prev) => {
            const list = [...(prev.fields || [])];
            list[index] = { ...list[index], ...patch };
            return { fields: list };
        });
    };

    const addProductField = () => {
        setDraft((prev) => {
            const list = [...(prev[activeType]?.fields || [])];
            list.push({ title: `사양${list.length + 1}`, inputType: 'dropdown' });
            return { ...prev, [activeType]: { fields: list } };
        });
    };

    const addAsRepairField = () => {
        setActiveAsRepairDraft((prev) => {
            const list = [...(prev.fields || [])];
            const n = list.length + 1;
            list.push({ key: `custom_${n}`, label: `항목${n}`, inputType: 'text', required: false });
            return { fields: list };
        });
    };

    const removeProductField = (index) => {
        setDraft((prev) => {
            const list = [...(prev[activeType]?.fields || [])];
            if (list.length <= 1) {
                showAppToast('사양 항목은 최소 1개 이상이어야 합니다.', 'error');
                return prev;
            }
            list.splice(index, 1);
            return { ...prev, [activeType]: { fields: list } };
        });
    };

    const removeAsRepairField = (index) => {
        setActiveAsRepairDraft((prev) => {
            const list = [...(prev.fields || [])];
            const target = list[index];
            if (AS_REPAIR_BUILTIN_KEYS.has(target?.key) && list.filter((f) => AS_REPAIR_BUILTIN_KEYS.has(f.key)).length <= 2) {
                showAppToast('기본 항목(현장명·제품명)은 유지해야 합니다.', 'error');
                return prev;
            }
            if (list.length <= 1) {
                showAppToast('항목은 최소 1개 이상이어야 합니다.', 'error');
                return prev;
            }
            list.splice(index, 1);
            return { fields: list };
        });
    };

    const moveProductField = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= productFields.length) {
            return;
        }
        setDraft((prev) => {
            const list = [...(prev[activeType]?.fields || [])];
            const [item] = list.splice(index, 1);
            list.splice(target, 0, item);
            return { ...prev, [activeType]: { fields: list } };
        });
    };

    const moveAsRepairField = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= asRepairFields.length) {
            return;
        }
        setActiveAsRepairDraft((prev) => {
            const list = [...(prev.fields || [])];
            const [item] = list.splice(index, 1);
            list.splice(target, 0, item);
            return { fields: list };
        });
    };

    const handleResetType = () => {
        const label = TYPE_LABELS[activeType];
        const ok = window.confirm(`${label} 설정을 기본값으로 되돌릴까요?`);
        if (!ok) {
            return;
        }
        if (isAnyAsRepairTab) {
            if (isAsRepairPersonalTab) {
                setAsRepairPersonalDraft(buildDefaultAsRepairFormConfig());
            } else {
                setAsRepairDraft(buildDefaultAsRepairFormConfig());
            }
            return;
        }
        const defaults = buildDefaultProductRegistrationConfig();
        setDraft((prev) => ({
            ...prev,
            [activeType]: { fields: [...defaults[activeType].fields] },
        }));
    };

    const handleSave = async () => {
        if (!currentUserName) {
            showAppToast('로그인이 필요합니다.', 'error');
            return;
        }
        const normalizedProduct = normalizeProductRegistrationConfig(draft);
        const normalizedAsRepair = normalizeAsRepairFormConfig(asRepairDraft);
        const normalizedAsRepairPersonal = normalizeAsRepairFormConfig(asRepairPersonalDraft);

        const emptyProductTitle = PRODUCT_TYPES.some((typeKey) => (
            normalizedProduct[typeKey].fields.some((f) => !String(f.title || '').trim())
        ));
        if (emptyProductTitle) {
            showAppToast('비어 있는 사양 제목이 있습니다. 제목을 입력해주세요.', 'error');
            return;
        }

        const emptyAsRepairLabel = normalizedAsRepair.fields.some((f) => !String(f.label || '').trim());
        if (emptyAsRepairLabel) {
            showAppToast('비어 있는 A/S 리스트 항목 제목이 있습니다.', 'error');
            return;
        }

        const emptyAsRepairPersonalLabel = normalizedAsRepairPersonal.fields.some(
            (f) => !String(f.label || '').trim(),
        );
        if (emptyAsRepairPersonalLabel) {
            showAppToast('비어 있는 A/S 개인 관리 항목 제목이 있습니다.', 'error');
            return;
        }

        const keys = normalizedAsRepair.fields.map((f) => f.key);
        if (new Set(keys).size !== keys.length) {
            showAppToast('A/S 리스트 항목 키가 중복되었습니다. 키를 확인해주세요.', 'error');
            return;
        }

        const personalKeys = normalizedAsRepairPersonal.fields.map((f) => f.key);
        if (new Set(personalKeys).size !== personalKeys.length) {
            showAppToast('A/S 개인 관리 항목 키가 중복되었습니다. 키를 확인해주세요.', 'error');
            return;
        }

        setBusy(true);
        try {
            const res = await fetch(`${getApiBaseUrl()}/product-registration-config`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: currentUserName,
                    adjuster: normalizedProduct.adjuster,
                    controller: normalizedProduct.controller,
                    asRepair: normalizedAsRepair,
                    asRepairPersonal: normalizedAsRepairPersonal,
                }),
            });
            const data = await readJsonResponse(res);
            const savedProduct = normalizeProductRegistrationConfig(data?.config || normalizedProduct);
            const savedAsRepair = normalizeAsRepairFormConfig(data?.config?.asRepair || normalizedAsRepair);
            const savedAsRepairPersonal = normalizeAsRepairFormConfig(
                data?.config?.asRepairPersonal || normalizedAsRepairPersonal,
            );
            setDraft(savedProduct);
            setAsRepairDraft(savedAsRepair);
            setAsRepairPersonalDraft(savedAsRepairPersonal);
            onConfigSaved(savedProduct, savedAsRepair, savedAsRepairPersonal);
            showAppToast('설정이 저장되었습니다.', 'success');
        } catch (error) {
            showAppToast(error?.message || '저장에 실패했습니다.', 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="placeholder-page product-registration-settings-page">
            <div className="product-reg-settings-head">
                <div>
                    <h3>제품 등록 설정</h3>
                    <p className="product-reg-settings-help">
                        {isAnyAsRepairTab
                            ? (isAsRepairPersonalTab
                                ? 'A/S 개인 관리의 「A/S 목록 추가」 화면에 표시할 항목·입력 방식·필수 여부를 설정합니다.'
                                : 'A/S 수리 관리의 「A/S 목록 추가」 화면에 표시할 항목·입력 방식·필수 여부를 설정합니다.')
                            : '제품 등록 화면에 표시할 사양 항목(제목)과 입력 방식을 설정합니다. 이미지·펌웨어·결선도·비고 항목은 고정으로 유지됩니다.'}
                    </p>
                </div>
                <div className="product-reg-settings-head-actions">
                    <button type="button" className="button button-cancel" onClick={() => reload().catch((e) => setLoadError(e.message))} disabled={busy}>
                        새로고침
                    </button>
                    <button type="button" className="button" onClick={handleSave} disabled={busy}>
                        {busy ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>

            {loadError && <div className="error-box">{loadError}</div>}

            <div className="product-reg-settings-type-tabs">
                {PRODUCT_TYPES.map((typeKey) => (
                    <button
                        key={typeKey}
                        type="button"
                        className={`button type-filter-button ${activeType === typeKey ? '' : 'off'} ${typeKey === 'adjuster' ? 'adjuster' : 'controller'}`}
                        onClick={() => setActiveType(typeKey)}
                        disabled={busy}
                    >
                        {TYPE_LABELS[typeKey]}
                    </button>
                ))}
                <button
                    type="button"
                    className={`button type-filter-button ${activeType === 'asRepair' ? '' : 'off'} as-repair-tab`}
                    onClick={() => setActiveType('asRepair')}
                    disabled={busy}
                >
                    {TYPE_LABELS.asRepair}
                </button>
                <button
                    type="button"
                    className={`button type-filter-button ${activeType === 'asRepairPersonal' ? '' : 'off'} as-repair-tab`}
                    onClick={() => setActiveType('asRepairPersonal')}
                    disabled={busy}
                >
                    {TYPE_LABELS.asRepairPersonal}
                </button>
            </div>

            <div className="product-reg-settings-toolbar">
                <button type="button" className="button" onClick={isAnyAsRepairTab ? addAsRepairField : addProductField} disabled={busy}>
                    {isAnyAsRepairTab ? '항목 추가' : '사양 항목 추가'}
                </button>
                <button type="button" className="button button-cancel" onClick={handleResetType} disabled={busy}>기본값 복원</button>
            </div>

            {isAnyAsRepairTab ? (
                <div className="product-reg-settings-table-wrap">
                    <table className="product-reg-settings-table">
                        <thead>
                            <tr>
                                <th>순서</th>
                                <th>항목 키</th>
                                <th>항목 제목</th>
                                <th>입력 방식</th>
                                <th>필수</th>
                                <th>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {asRepairFields.map((field, index) => (
                                <tr key={`asRepair-${field.key}-${index}`}>
                                    <td className="product-reg-settings-order">{index + 1}</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="field-input product-reg-settings-key-input"
                                            value={field.key}
                                            onChange={(e) => updateAsRepairField(index, { key: e.target.value })}
                                            disabled={busy || AS_REPAIR_BUILTIN_KEYS.has(field.key)}
                                            title={AS_REPAIR_BUILTIN_KEYS.has(field.key) ? '기본 항목 키는 변경할 수 없습니다' : ''}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="field-input"
                                            value={field.label}
                                            onChange={(e) => updateAsRepairField(index, { label: e.target.value })}
                                            placeholder="팝업에 표시할 제목"
                                            disabled={busy}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="field-input"
                                            value={field.inputType}
                                            onChange={(e) => {
                                                const nextType = e.target.value;
                                                const patch = { inputType: nextType };
                                                if (nextType !== 'auto') {
                                                    patch.formula = '';
                                                } else if (!String(field.formula || '').trim()) {
                                                    const suggested = suggestAsRepairAutoFormula(field.label);
                                                    if (suggested) {
                                                        patch.formula = suggested;
                                                    }
                                                }
                                                updateAsRepairField(index, patch);
                                            }}
                                            disabled={busy}
                                        >
                                            {AS_REPAIR_INPUT_TYPE_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        {field.inputType === 'auto' ? (
                                            <input
                                                type="text"
                                                className="field-input as-repair-formula-input"
                                                value={field.formula || ''}
                                                onChange={(e) => updateAsRepairField(index, { formula: e.target.value })}
                                                placeholder={DEFAULT_INCOMING_QTY_FORMULA}
                                                disabled={busy}
                                                title="필드 키를 이용한 계산식. 적용 로직은 추후 연동됩니다."
                                            />
                                        ) : null}
                                    </td>
                                    <td className="product-reg-settings-required-cell">
                                        <input
                                            type="checkbox"
                                            checked={!!field.required}
                                            onChange={(e) => updateAsRepairField(index, { required: e.target.checked })}
                                            disabled={busy}
                                            aria-label={`${field.label} 필수`}
                                        />
                                    </td>
                                    <td className="product-reg-settings-actions">
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => moveAsRepairField(index, -1)} disabled={busy || index === 0}>▲</button>
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => moveAsRepairField(index, 1)} disabled={busy || index === asRepairFields.length - 1}>▼</button>
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => removeAsRepairField(index)} disabled={busy}>삭제</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="product-reg-settings-table-wrap">
                    <table className="product-reg-settings-table">
                        <thead>
                            <tr>
                                <th>순서</th>
                                <th>사양 제목</th>
                                <th>입력 방식</th>
                                <th>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productFields.map((field, index) => (
                                <tr key={`${activeType}-${index}`}>
                                    <td className="product-reg-settings-order">{index + 1}</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="field-input"
                                            value={field.title}
                                            onChange={(e) => updateProductField(index, { title: e.target.value })}
                                            placeholder="사양 제목"
                                            disabled={busy}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="field-input"
                                            value={field.inputType}
                                            onChange={(e) => updateProductField(index, { inputType: e.target.value })}
                                            disabled={busy}
                                        >
                                            <option value="dropdown">드롭다운</option>
                                            <option value="text">직접 입력</option>
                                            <option value="date">날짜 입력</option>
                                        </select>
                                    </td>
                                    <td className="product-reg-settings-actions">
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => moveProductField(index, -1)} disabled={busy || index === 0}>▲</button>
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => moveProductField(index, 1)} disabled={busy || index === productFields.length - 1}>▼</button>
                                        <button type="button" className="button button-cancel model-edit-button" onClick={() => removeProductField(index)} disabled={busy}>삭제</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isAnyAsRepairTab ? (
                <div className="product-reg-settings-fixed">
                    <h4>자동 입력 안내</h4>
                    <ul>
                        <li>입력 방식 「자동 입력」: A/S 추가·세부 화면에서 직접 수정 불가, 계산된 값만 표시됩니다.</li>
                        <li>입고수량 예: <code>{DEFAULT_INCOMING_QTY_FORMULA}</code> — 「전체 수량」「수량 부족」 항목 제목과 일치해야 합니다.</li>
                        <li>무상 수리 수량 예: <code>{DEFAULT_FREE_REPAIR_QTY_FORMULA}</code> — 「입고 수량」「반품 수량」「유상 수리 수량」 항목 제목과 일치해야 합니다.</li>
                    </ul>
                </div>
            ) : (
                <div className="product-reg-settings-fixed">
                    <h4>고정 항목 (제품 등록 화면 하단)</h4>
                    <ul>
                        <li>{activeType === 'controller' ? '제어기 이미지' : '조절기 이미지'} — 파일 업로드</li>
                        <li>펌웨어 파일 — 파일 업로드</li>
                        <li>결선도/발주서/체크리스트 — 파일 업로드</li>
                        <li>비고 — 직접 입력</li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ProductRegistrationSettingsPage;
